import { useCallback } from 'react';

import { useLocalStorage } from '../../../helpers/localStorage';

const STORAGE_KEY = '__FOSSA__fossabotPrJobs';
/** Creation jobs are short-lived; drop stale entries so a jobId can't linger forever. */
const TTL_MS = 60 * 60 * 1000; // 1 hour

type StoredJob = {
  jobId: string;
  createdAt: number;
};

type FossabotPrJobsStore = Record<string, StoredJob>;

// An issue can affect multiple projects, so scope the key by issue + project.
const keyFor = (issueId: number, projectLocator?: string): string =>
  projectLocator ? `${issueId}:${projectLocator}` : `${issueId}`;

/**
 * A localStorage-backed store for in-flight fossabot PR creation jobIds.
 *
 * The backend status endpoint can only report the 'creating' state (and a
 * failed creation) when it is given the jobId, so persisting it here lets
 * polling resume after a page reload during the creation window. Once a real
 * PR exists the endpoint resolves it by repository + dependency, and the entry
 * can be cleared.
 */
export function useFossabotPrJobStore() {
  const ls = useLocalStorage<FossabotPrJobsStore>(STORAGE_KEY);

  const getJobId = useCallback((issueId: number, projectLocator?: string): string | undefined => {
    const key = keyFor(issueId, projectLocator);
    const entry = (ls.get() ?? {})[key];
    if (!entry) {
      return undefined;
    }
    // A negative age means createdAt is in the future (clock skew or a tampered
    // value), so treat it as expired rather than letting it linger until the
    // clock catches up.
    const ageMs = Date.now() - entry.createdAt;
    if (ageMs < 0 || ageMs > TTL_MS) {
      return undefined;
    }
    return entry.jobId;
  }, [ls]);

  const setJobId = useCallback((issueId: number, projectLocator: string | undefined, jobId: string): void => {
    const key = keyFor(issueId, projectLocator);
    const store = ls.get() ?? {};
    ls.set({ ...store, [key]: { jobId, createdAt: Date.now() } });
  }, [ls]);

  const clearJobId = useCallback((issueId: number, projectLocator?: string): void => {
    const key = keyFor(issueId, projectLocator);
    const { [key]: omitted, ...rest } = ls.get() ?? {};
    ls.set(rest);
  }, [ls]);

  return { getJobId, setJobId, clearJobId };
}
